import { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts]     = useState([]);
  const [confirm, setConfirm]   = useState(null); // { message, confirmLabel, danger, onConfirm }
  const timerRef = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timerRef.current[id]);
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  // A longer sentence can ask to stay longer; everything else keeps four seconds.
  // A message may also carry one action -- "Close the gap" -- which is offered,
  // never done: pressing it is the only way it happens.
  const addToast = useCallback((message, type = 'info', ms = 4000, action = null) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type, action, ms }]);
    timerRef.current[id] = setTimeout(() => dismiss(id), ms);
  }, [dismiss]);

  // Reading it keeps it: the pointer on a message stops it fading, and it
  // fades a few seconds after the pointer leaves.
  const hold    = useCallback((id) => clearTimeout(timerRef.current[id]), []);
  const release = useCallback((id) => {
    clearTimeout(timerRef.current[id]);
    timerRef.current[id] = setTimeout(() => dismiss(id), 3000);
  }, [dismiss]);

  const toast = {
    success: (msg, ms) => addToast(msg, 'success', ms),
    error:   (msg, ms) => addToast(msg, 'error', ms),
    warning: (msg, ms) => addToast(msg, 'warning', ms),
    info:    (msg, ms) => addToast(msg, 'info', ms),
    offer:   (msg, action, ms = 12000) => addToast(msg, 'info', ms, action),
  };

  function showConfirm({ message, confirmLabel = 'Confirm', danger = false, onConfirm }) {
    setConfirm({ message, confirmLabel, danger, onConfirm });
  }

  function handleConfirm() {
    confirm?.onConfirm?.();
    setConfirm(null);
  }

  const TYPE_STYLES = {
    success: { bg: '#14532d', border: '#16a34a', color: '#86efac', icon: '✓' },
    error:   { bg: '#7f1d1d', border: '#dc2626', color: '#fca5a5', icon: '✕' },
    warning: { bg: '#78350f', border: '#d97706', color: '#fcd34d', icon: '⚠' },
    info:    { bg: '#1e3a5f', border: '#3b82f6', color: '#93c5fd', icon: 'i' },
  };

  return (
    <ToastContext.Provider value={{ toast, confirm: showConfirm }}>
      {children}

      {/* Toast stack — bottom right */}
      <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9000, display: 'flex', flexDirection: 'column', gap: '8px', pointerEvents: 'none' }}>
        {toasts.map(t => {
          const s = TYPE_STYLES[t.type] || TYPE_STYLES.info;
          return (
            <div key={t.id} onMouseEnter={() => hold(t.id)} onMouseLeave={() => release(t.id)}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: '10px', padding: '12px 14px', minWidth: '260px', maxWidth: 'min(380px, calc(100vw - 32px))', boxShadow: '0 4px 16px rgba(0,0,0,0.4)', fontFamily: 'system-ui, sans-serif', pointerEvents: 'all', animation: 'fadeInUp 0.2s ease' }}>
              <span style={{ fontSize: '12px', fontWeight: '800', color: s.color, marginTop: '1px', flexShrink: 0 }}>{s.icon}</span>
              <span style={{ fontSize: '13px', color: s.color, flex: 1, lineHeight: 1.4 }}>
                {t.message}
                {t.action && (
                  <button onClick={() => { dismiss(t.id); t.action.onClick(); }}
                    style={{ display: 'block', marginTop: '8px', background: 'rgba(20,184,166,0.15)', border: '1px solid #14b8a6', color: '#5eead4', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui, sans-serif' }}>
                    {t.action.label}
                  </button>
                )}
              </span>
              <button onClick={() => dismiss(t.id)} style={{ background: 'none', border: 'none', color: s.color, cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0', opacity: 0.7, flexShrink: 0 }}>×</button>
            </div>
          );
        })}
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}
          onClick={() => setConfirm(null)}>
          <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '360px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f2f5', marginBottom: '8px' }}>Are you sure?</div>
            <div style={{ fontSize: '13px', color: '#9ca3af', lineHeight: 1.5, marginBottom: '20px' }}>{confirm.message}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setConfirm(null)} style={{ background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '9px 16px', fontSize: '13px', cursor: 'pointer', fontFamily: 'system-ui' }}>
                Cancel
              </button>
              <button onClick={handleConfirm} style={{ background: confirm.danger ? '#dc2626' : '#14b8a6', border: 'none', color: '#fff', borderRadius: '8px', padding: '9px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui' }}>
                {confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
